import { matchFormatLabel } from './match-format';

export interface SetScore {
  p1: number;
  p2: number;
}

export interface MatchPlayer {
  memberId: number;
  name: string;
  // Player.id każdej osoby po tej stronie — po nich buduje się link do profilu.
  playerIds?: number[];
}

export interface MatchRules {
  bestOf: number;
  pointsPerSet: number;
  withAdvantage: boolean;
  betterPoint: number | null;
  setsPlayed?: number | null;
}

export interface SetEntryOptions {
  maxScore: number;
  winnerByLoser: Record<string, number>;
}

export interface SideRating {
  rating: number;
  deltaIfWin?: number;
  deltaIfLose?: number;
}

export interface MatchRating {
  side1: SideRating;
  side2: SideRating;
  appliedDelta: { side1: number; side2: number } | null;
  // Kolejka na better point waży deltę wynikiem — widełki mówią o zwykłej wygranej.
  scoreWeighted?: boolean;
}

export interface MatchItem {
  id: number;
  status: string;
  groupName: string | null;
  // Numer rundy kolejki rotacyjnej; null/brak w zwykłej kolejce.
  runNumber?: number | null;
  rules: MatchRules;
  setEntry: SetEntryOptions;
  player1Name: string;
  player2Name: string;
  side1: MatchPlayer[];
  side2: MatchPlayer[];
  sets: SetScore[];
  winnerSide: 'SIDE1' | 'SIDE2' | null;
  // Skończone sety meczu, który jeszcze trwa — nie wynik, tabela ich nie liczy.
  partialSets?: SetScore[] | null;
  rating?: MatchRating | null;
}

// Jedna składowa punktów klasyfikacji — „2 × 10 za rozegrane mecze".
export interface ClassificationLine {
  label: string;
  detail: string;
  points: number;
}

export interface StandingRow {
  rank: number;
  leaguePlayerId: number;
  playerIds?: number[];
  name: string;
  played: number;
  wins: number;
  losses: number;
  setsWon: number;
  setsLost: number;
  points: number;
  smallPoints: number;
  smallPointsLost: number;
  placementPoints: number;
  // Skąd wzięły się `placementPoints`; starsze API jej nie wysyła.
  classificationBreakdown?: ClassificationLine[];
  rating: number | null;
  ratingDelta?: number;
  draws?: number;
  buchholz?: number;
  // Rundy americano przesiedziane na pauzie — punkty za nie są już w `points`.
  restedRuns?: number;
  // Numery rund z pauzą — modal pokazuje je między meczami.
  restedRunNumbers?: number[];
  // Czym ten wiersz wyszedł z remisu — null, gdy nikogo nie wyprzedził.
  decidedBy?: {
    criterion: string;
    label: string;
    ownValue: number | null;
    rivalValue: number | null;
    rivalNames: string[];
  } | null;
}

export interface ScoringSystem {
  perSet: boolean;
  winPoints: number;
  drawPoints: number;
  lossPoints: number;
  dominantMargin: number | null;
  dominantWinPoints: number | null;
  dominantLossPoints: number | null;
}

// Czym rozstrzygany jest remis w tej tabeli — API liczy kolejność, viewer tylko
// mówi o niej czytającemu.
export interface TiebreakInfo {
  order: string[];
  custom: boolean;
  hint: string;
  // Rozpisane kroki do okna wyjaśniającego — liczy je API, bo opisy kryteriów
  // mieszkają w league-rules, a viewer nie ma jak stamtąd importować.
  steps?: Array<{ label: string; description: string; primary: boolean }>;
}

export interface ScoringGroup {
  id: number | null;
  name: string | null;
  rules: MatchRules;
  scoring?: ScoringSystem;
  tiebreak?: TiebreakInfo;
  matchCount: number;
  playedCount: number;
  // Ile punktów daje jedna pauza w americano; null poza americano. Liczbę zna API,
  // viewer nie ma jej skąd zaszyć.
  restingRunPoints?: number | null;
  standings: StandingRow[];
}

export interface RoundInfo {
  id: number;
  name: string;
  number: number;
  startDate: string;
  shareToken?: string;
  league: { id: number; name: string; discipline: string };
  season: { id: number; number: number; name: string };
}

// Kolejka rotacyjna (americano / mexicano / king of the court) gra rundami
// dokładanymi w trakcie — tego nie ma zwykła kolejka rozpisana z góry.
export interface RotationState {
  format: string;
  formatTitle: string;
  nextRunNumber: number;
  dealtRuns: number;
  missingResults: number;
  canDealNextRun: boolean;
  blockedReason: string | null;
}

export interface ScoringResponse {
  status: string;
  canSubmit: boolean;
  playMode?: string | null;
  rotation?: RotationState | null;
  round: RoundInfo;
  groups?: ScoringGroup[];
  matches: MatchItem[];
  // Ile z delty ELO niesie mecz tej kolejki (americano 0.5). Liczy API — front tylko pokazuje.
  ratingWeight?: number;
}

export function ratingWeightLabel(ratingWeight: number | undefined): string | null {
  if (ratingWeight == null || ratingWeight === 1) {
    return null;
  }

  return `×${ratingWeight.toString().replace('.', ',')}`;
}

export function groupFormatLabel(group: ScoringGroup): string {
  return matchFormatLabel(group.rules, group.scoring);
}

export function smallPointsBalance(row: StandingRow): number {
  return row.smallPoints - row.smallPointsLost;
}

export function usesPointSystem(group: ScoringGroup): boolean {
  return group.scoring?.perSet === true;
}

export function groupKey(group: ScoringGroup): string {
  return group.name ?? '__all__';
}

export function matchesOf(matches: MatchItem[], group: ScoringGroup): MatchItem[] {
  return matches.filter((match) => match.groupName === group.name);
}

export function formatDelta(value: number): string {
  if (value > 0) {
    return `+${value}`;
  }

  return String(value).replace('-', '−');
}

export function formatBuchholz(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export function deltaTone(value: number): string {
  if (value > 0) {
    return 'scoring-delta scoring-delta--up';
  }

  if (value < 0) {
    return 'scoring-delta scoring-delta--down';
  }

  return 'scoring-delta';
}

export function resolveGroups(response: ScoringResponse): ScoringGroup[] {
  if (response.groups && response.groups.length > 0) {
    return response.groups;
  }

  return response.matches.reduce<ScoringGroup[]>((groups, match) => {
    const name = match.groupName ?? null;
    const existing = groups.find((group) => group.name === name);
    const played = match.winnerSide !== null || match.sets.length > 0 ? 1 : 0;

    if (existing) {
      existing.matchCount += 1;
      existing.playedCount += played;

      return groups;
    }

    return [
      ...groups,
      { id: null, name, rules: match.rules, matchCount: 1, playedCount: played, standings: [] },
    ];
  }, []);
}
